<template>
  <modal
    v-if="show"
    height="640px"
    width="1200px"
    bodyPadding="0"
    :title="t('Bulk Edit Offers')"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/offer-settings`"
  >
    <div class="dialog-wrapper">
      <ui-info class="info-label" width>
        {{t('You have selected {0} offers. Mark the checkboxes on the left and set up values for the parameters which should be edited in bulk.', [selectedItems.length])}}
      </ui-info>
      <div class="d-flex bulk-edit-columns">
        <div class="details bulk-edit-columns__panel">
          <div class="subtitle">
            1. {{t('URL Parts')}}
          </div>
          <div class="wrap">
            <div
                class="d-flex items-container"
                :class="{'container-disabled':!checkboxState['protocol']}"
            >
              <ui-checkbox v-model="checkboxState['protocol']" class="checkbox"></ui-checkbox>
              <ui-select
                  :label="t('Domain’s Protocol')"
                  :label-help="t('Select the domain’s protocol. If not selected, BeMob will add the protocol which is available in the URL field of the offers you are editing in bulk.')"
                  :placeholder="t('Select the domain’s protocol')"
                  class="element-container"
                  v-model="form.protocol"
                  :disabled="!checkboxState['protocol']"
                  :hide-details="1"
                  :items="['http', 'https', 'market', 'itms-apps', 'itms']"
                  :error-messages="errorsDict['protocol']"
              ></ui-select>
            </div>
            <div class="d-flex items-container mt-8" :class="{'container-disabled':!checkboxState['domain']}">
              <ui-checkbox v-model="checkboxState['domain']" class="checkbox"></ui-checkbox>
              <ui-input
                  v-model="form.domain"
                  :disabled="!checkboxState['domain']"
                  class="element-container"
                  :placeholder="t('e.g. www.domain.com')"
                  :label="t('Domain')"
                  :label-help="t('Specify the domain which should be added instead of the initial domain for your offers. It should be added in the format xxxx.domain.com or Domain.com')"
                  :hide-details="1"
                  :error-messages="errorsDict['domain']"
              ></ui-input>
            </div>
            <div class="d-flex items-container mt-8" :class="{'container-disabled':!checkboxState['path']}">
              <ui-checkbox v-model="checkboxState['path']" class="checkbox"></ui-checkbox>
              <ui-input
                  v-model="form.path"
                  :disabled="!checkboxState['path']"
                  class="element-container"
                  :placeholder="t('e.g. /home')"
                  :label="t('Path')"
                  :label-help="t('If any path is available in your offer page it should be specified as /home/go')"
                  :hide-details="1"
                  :error-messages="errorsDict['path']"
              ></ui-input>
            </div>
            <div class="d-flex items-container mt-8" :class="{'container-disabled':!checkboxState['query']}">
              <ui-checkbox v-model="checkboxState['query']" class="checkbox"></ui-checkbox>
              <ui-create-url-tags
                :placeholder="t('e.g. c={campaignId}')"
                v-model="form.query"
                :showQr="false"
                :textAreaRowsCount="5"
                :toolTip="t(`Add all the required BeMob tokens along with the parameters to pass the additional information to your affiliate network or direct advertiser. Each parameter + token should be separated from each other with '&\'\, e.g. c={'{'}campaignId{'}'}&city={'{'}city{'}'}`)"
                :disabled="!checkboxState['query']"
                :chips="chips"
                class="element-container mt-4"
                :label="t('Query Params & Tokens')"
              ></ui-create-url-tags>
            </div>
          </div>
        </div>

        <div class="details bulk-edit-columns__panel">
          <div class="subtitle">
            2. {{ t('Other Parameters') }}
          </div>
          <div class="wrap">
            <div class="d-flex items-container mt-4" :class="{'container-disabled':!checkboxState['tags']}">
              <ui-checkbox v-model="checkboxState['tags']" class="checkbox"></ui-checkbox>
              <ui-select
                  planLimits="tagsOffers"
                  v-model="form.tags"
                  :disabled="!checkboxState['tags']"
                  class="element-container"
                  :placeholder="t('Enter tag values...')"
                  :label="t('Tags')"
                  item-text="name"
                  :label-help="t('Add personalized tags to search and navigate easy through offers list.')"
                  :items="store.cache.tags.offers.data"
                  :hide-details="2"
                  multiple
                  chips
                  attach
                  combobox
                  multi-line
                  :error-messages="errorsDict['tags']"
              >
                <template #no-data>
                  <no-tag-found></no-tag-found>
                </template>
              </ui-select>
            </div>
            <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['currencyId']}">
              <ui-checkbox v-model="checkboxState['currencyId']" class="checkbox"></ui-checkbox>
              <div class="element-container">
                <ui-select
                    v-model="form.currencyId"
                    :disabled="!checkboxState['currencyId']"
                    :placeholder="t('Currency')"
                    :label="t('Currency')"
                    :label-help="t('Set revenue currency for this offer. Offer currency will automatically be converted to your account currency in reports.')"
                    :items="currencyListOrderedPreferred"
                    item-text="name"
                    item-value="code"
                    :return-object="false"
                    :hide-details="2"
                    attach
                    :error-messages="errorsDict['currencyId']"
                ></ui-select>
                <div class="d-flex">
                  <ui-radio-group
                      v-model="form.payoutType"
                      class="w50 payout-type"
                      :disabled="!checkboxState['currencyId']"
                      :label="t('Payout type')"
                  >
                    <ui-radio :label="t('Auto')" value="auto"></ui-radio>
                    <ui-radio :label="t('Manual')" value="manual"></ui-radio>
                  </ui-radio-group>
                  <div v-show="form.payoutType === 'manual'" class="w50">
                    <ui-input
                        v-model="form.payoutSum"
                        :disabled="!checkboxState['currencyId']"
                        :label="t('Payout Sum')"
                        :prefix="currencies[form.currencyId]"
                        :has-errors="!!errorsDict['payoutSum']"
                    ></ui-input>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <template #footer>
      <section
        class="justify-space-between align-center xs12"
        style="display: flex; width: 100%"
      >
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive"></error-aggregator>
        </div>

        <div class="d-flex justify-end align-center">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          ></ui-button>

          <ui-button
            data-action="save"
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            @click="submit"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>


<script setup lang="ts">
import NoTagFound from '@/views/reports/views/no-tag-found.vue';
import { useOfferBulkEdit } from './useOfferBulkEdit';

const emit = defineEmits<{
  close: [];
}>();

const {
  t,
  locale,
  store,

  show,
  form,
  checkboxState,
  selectedItems,
  currencyListOrderedPreferred,
  currencies,
  chips,
  errors,
  errorsDict,
  errorsControlActive,
  checkClose,
  submit,
} = useOfferBulkEdit(() => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.bulk-edit-columns {
  &__panel {
    flex: 1 1 100%;
    min-width: 0;

    @media (min-width: 960px) {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }
}

.dialog-wrapper {
  overflow-y: auto;
  height: 100%;
}

@media (min-width: 960px) {
  .dialog-wrapper {
    overflow: auto;
    .details {
      overflow: hidden;
    }
  }
}

.container-disabled {
  .element-container {
    opacity: 0.7;

    :deep(.v-input--disabled),
    :deep(.v-field--disabled) {
      opacity: 1;
    }
  }
}

.details {
  .title {
    h3 {
      padding: 10px;
    }
    border-bottom: 1px solid var(--color-gray10);
  }

  .wrap {
    padding: 16px;
  }

}

.url-label {
  color: var(--color-graphite);
  font-weight: bold;
  font-size: 16px;
}

.url-container {
  padding-bottom: 10px;
  padding-top: 4px;
  border-top: 1px solid var(--color-gray10);
  border-bottom: 1px solid var(--color-gray10);
}

.payout-type {
  margin-top: 0!important;
  padding-top: 0!important;
}

.info-label {
  height: fit-content;
  margin: 10px;
}

.main-container {
  flex-direction: column;
  overflow-y: auto!important;
  height: 100%;
}

.element-container {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  margin-top: 3px;
  max-width: calc(100% - 28px);

  > :deep(*) {
    width: 100%;
    max-width: 100%;
  }

  :deep(.v-input) {
    width: 100%;
    max-width: 100%;
  }
}

.items-container {
  display: flex;
  align-items: flex-start;
  flex-grow: 0;
}

.checkbox {
  margin-right: 8px;
  padding-top: 5px;
  flex-grow: 0!important;
}
</style>
